import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

interface User {
  id: number;
  email: string;
  full_name: string;
  two_factor_enabled: boolean;
  role: string;  
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ requires2FA: boolean }>;
  verify2FA: (email: string, code: string) => Promise<void>;
  register: (email: string, password: string, fullName: string, token: string) => Promise<void>;  
  logout: () => void;
  loginWithMicrosoft: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Load user on mount
  useEffect(() => {
    const loadUser = async () => {
      const storedToken = localStorage.getItem('token');
      console.log('🟡 AuthContext: Loading user, token exists:', !!storedToken);
      
      if (storedToken) {
        try {
          // Set Authorization header
          const config = {
            headers: {
              'Authorization': `Bearer ${storedToken}`
            }
          };
          
          console.log('🟡 Making request to /auth/me with token');
          const response = await axios.get(`${API_URL}/auth/me`, config);
          console.log('🟢 User loaded:', response.data);
          
          setUser(response.data);
          setToken(storedToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        } catch (error) {
          console.error('🔴 Error loading user:', error);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    
    loadUser();
  }, []);

  const login = async (email: string, password: string) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    
    if (response.data.user.requires_2fa) {
      return { requires2FA: true };
    }
    
    const { access_token, user } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(user);
    
    return { requires2FA: false };
  };

  const verify2FA = async (email: string, code: string) => {
    const response = await axios.post(`${API_URL}/auth/verify-2fa`, { email, code });
    
    const { access_token, user } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(user);
  };

  const register = async (email: string, password: string, fullName: string, token: string) => {
    const response = await axios.post(`${API_URL}/auth/register?token=${token}`, {
      email,
      password,
      full_name: fullName,
    });
    
    const { access_token, user } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(user);
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const loginWithMicrosoft = async () => {
    try {
      console.log('🟢 loginWithMicrosoft: Starting...');
      console.log('🟢 API_URL:', API_URL);
      
      const response = await axios.get(`${API_URL}/auth/microsoft`);
      console.log('🟢 Response received:', response.data);
      
      const authUrl = response.data.auth_url;
      console.log('🟢 Auth URL:', authUrl);
      
      if (!authUrl) {
        throw new Error('No auth_url in response');
      }
      
      console.log('🟢 Redirecting to Microsoft...');
      window.location.href = authUrl;
    } catch (error) {
      console.error('🔴 Microsoft login error:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    token,
    loading,
    login,
    verify2FA,
    register,
    logout,
    loginWithMicrosoft,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
